import type { NoteElementSelection } from './App';
import type { Playback } from './audio/player';
import { getAbsoluteOffset } from './models/Note';

/**
 * Score following: while a playback runs (the whole piece or one entry), the written notes that sound light up in
 * the sheet (layout.css, `.is-sounding`). One playback runs at a time, so the lit notes are kept here for all of them.
 */
const soundingClass = 'is-sounding';
let lit = new Set<Element>();

/** A written note of the sheet: its element, its time span in quarter notes and its voice. */
export interface NoteSpan {
  element: Element;
  onset: number;
  end: number;
  voice: number | undefined;
}

/** The written notes of the sheet with their time spans; rests are left out. */
export const noteSpansOf = (noteElements: NoteElementSelection | undefined): NoteSpan[] => {
  if (!noteElements) return [];
  const contexts = noteElements.data();

  return noteElements
    .nodes()
    .map((element, index) => {
      const { note, voiceNumber } = contexts[index];
      const onset = getAbsoluteOffset(note.sourceNote);
      return {
        element,
        end: onset + note.sourceNote.Length.RealValue * 4,
        isRest: note.sourceNote.isRest(),
        onset,
        voice: voiceNumber
      };
    })
    .filter((span) => !span.isRest)
    .map(({ isRest: _, ...span }) => span);
};

/** Asks the A4 canvas (MelodyAnalysis), which has no scroll bars, to move the page so that a note is in view. */
export const revealEvent = 'melodyvis-reveal';

/** A zoomed sheet scrolls to the notes that are struck when they are out of its view. */
const keepInView = (element: Element) => {
  const viewport = element.closest('.melodyvis-sheet-viewport');
  if (!viewport) return;
  const view = viewport.getBoundingClientRect();
  const note = element.getBoundingClientRect();
  if (
    note.top < view.top ||
    note.bottom > view.bottom ||
    note.left < view.left ||
    note.right > view.right
  )
    if (viewport.classList.contains('is-a4'))
      viewport.dispatchEvent(new CustomEvent(revealEvent, { detail: element }));
    else element.scrollIntoView({ block: 'center', inline: 'center' });
};

/** Lights up the given notes, and only them. */
const light = (sounding: Set<Element>) => {
  lit.forEach((element) => {
    if (!sounding.has(element)) element.classList.remove(soundingClass);
  });
  const struck = [...sounding].filter((element) => !lit.has(element));
  sounding.forEach((element) => element.classList.add(soundingClass));
  lit = sounding;
  if (struck.length > 0) keepInView(struck[0]);
};

export const clearSounding = () => light(new Set());

export interface Following {
  /** Stops following and clears the lit notes. */
  stop: () => void;
  /** Stops following and leaves the notes lit (a paused piece shows where it stopped). */
  release: () => void;
}

/**
 * Follows a playback until it ends: the notes of `spans` (the latest written notes; zooming draws them anew) that
 * sound at its position light up. `offsetAt` turns the playback's position into an offset of the sheet (an entry plays
 * from its first note), `isFollowed` keeps the notes that play (an entry: its voice and bars), and `onOffset` hears
 * where the playback is (to turn the pages).
 */
export const followPlayback = (
  spans: () => NoteSpan[],
  playback: Playback,
  {
    isFollowed = () => true,
    offsetAt = (position) => position,
    onOffset
  }: {
    isFollowed?: (span: NoteSpan) => boolean;
    offsetAt?: (position: number) => number;
    onOffset?: (offset: number) => void;
  } = {}
): Following => {
  let source: NoteSpan[] | undefined;
  let followed: NoteSpan[] = [];
  let frame: number | undefined;
  let isActive = true;

  const step = () => {
    const current = spans();
    if (current !== source) {
      source = current;
      followed = current.filter(isFollowed);
    }
    const offset = offsetAt(playback.position());
    light(
      new Set(
        followed
          .filter((span) => span.onset <= offset + 1e-6 && offset < span.end - 1e-6)
          .map((span) => span.element)
      )
    );
    onOffset?.(offset);
    frame = window.requestAnimationFrame(step);
  };
  const halt = () => {
    isActive = false;
    if (frame !== undefined) window.cancelAnimationFrame(frame);
  };
  const stop = () => {
    halt();
    clearSounding();
  };

  playback.started.then(() => {
    if (isActive) frame = window.requestAnimationFrame(step);
  });
  playback.finished.then(() => {
    if (isActive) stop();
  });

  return { release: halt, stop };
};
